import { useState, useContext, useEffect, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError';
import { FrontCoverStateEnum } from '@confluence/route-manager/entry-points/companyHubUtils';
import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext';
import { COMPANY_HUB_CREATION_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout';

import { useCompanyHubPremiumGate } from '../upsell/useCompanyHubPremiumGate';

import { createCompanyHubSpace, hasStalePermissionsErrorMessage } from './createCompanyHubSpace';
import type { CreateCompanyHubSpaceResult } from './createCompanyHubSpace';

type UseCompanyHubOnRouteCreationFlowProps = {
	onSuccess?: (homepageId: CreateCompanyHubSpaceResult['homepageId']) => void;
	onExists?: (homepageId: CreateCompanyHubSpaceResult['homepageId']) => void;
	route?: string;
};

export function useCompanyHubOnRouteCreationFlow({
	onSuccess,
	onExists,
	route,
}: UseCompanyHubOnRouteCreationFlowProps): {
	isCreatingHub: boolean;
	creationError: ApolloErrorInterop | null;
} {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isCreatingHub, setIsCreatingHub] = useState<boolean>(false);
	const [creationError, setCreationError] = useState<ApolloErrorInterop | null>(null);
	const { isSiteAdmin, frontCoverState } = useContext(SPAViewContext);
	const experienceTracker = useContext(ExperienceTrackerContext);

	const shouldShowPremiumUpsell = useCompanyHubPremiumGate();

	const isCompanyHubUnset = frontCoverState === FrontCoverStateEnum.UNSET;
	const hasCreatedHub = useRef<boolean>(false);

	useEffect(() => {
		if (!hasCreatedHub.current) {
			const shouldCreateHub =
				isCompanyHubUnset && isSiteAdmin && !shouldShowPremiumUpsell && !isCreatingHub;

			if (shouldCreateHub) {
				let wasHubCreated = false;
				setIsCreatingHub(true);
				hasCreatedHub.current = true;
				experienceTracker.start({
					name: COMPANY_HUB_CREATION_EXPERIENCE,
					timeout: ExperienceTimeout.DEFAULT,
				});
				createCompanyHubSpace()
					.then(({ didCreateSpace, homepageId }) => {
						if (didCreateSpace) {
							experienceTracker.succeed({
								name: COMPANY_HUB_CREATION_EXPERIENCE,
							});
							wasHubCreated = true;
							onSuccess?.(homepageId);
						} else {
							experienceTracker.abort({
								name: COMPANY_HUB_CREATION_EXPERIENCE,
								reason:
									'Hub space already exists. Handled by redirecting user to Hub editor without first time experience',
							});
							onExists?.(homepageId);
						}
					})
					.catch((error) => {
						setCreationError(error);

						if (hasStalePermissionsErrorMessage(error)) {
							experienceTracker.abort({
								name: COMPANY_HUB_CREATION_EXPERIENCE,
								reason: 'User has stale admin permissions',
							});
						} else if (isUnauthorizedError(error)) {
							experienceTracker.abort({
								name: COMPANY_HUB_CREATION_EXPERIENCE,
								reason: 'User is not authenticated',
							});
						} else {
							experienceTracker.fail({
								name: COMPANY_HUB_CREATION_EXPERIENCE,
								error,
							});
						}
					})
					.finally(() => {
						setIsCreatingHub(false);

						createAnalyticsEvent({
							type: 'sendTrackEvent',
							data: {
								source: 'companyHub',
								action: 'created',
								actionSubject: 'onRouteStart',
								actionSubjectId: 'companyHubOnRouteCreation',
								attributes: {
									created: wasHubCreated,
									route,
								},
							},
						}).fire();
					});
			}
		}
	}, [
		isCompanyHubUnset,
		isSiteAdmin,
		createAnalyticsEvent,
		shouldShowPremiumUpsell,
		isCreatingHub,
		experienceTracker,
		onSuccess,
		onExists,
		route,
	]);

	return {
		isCreatingHub,
		creationError,
	};
}
